import { useCallback, useEffect, useContext } from "react";
import chatHub from "../services/ChatHub";
import { ChatContext } from "../context/chat.context";

export const useChatConnection = (companyId, actions) => {
  const connectToChat = useCallback(async () => {
    if (companyId === null || companyId === undefined || companyId === "") {
      return;
    }

    try {
      await chatHub.connect(companyId);
    } catch (error) {
      console.error("Failed to connect to chat:", error);
    }
  }, [companyId]);

  const disconnectFromChat = useCallback(async () => {
    try {
      await chatHub.disconnect();
      actions.resetState();
    } catch (error) {
      console.error("Failed to disconnect from chat:", error);
    }
  }, [actions]);

  return { connectToChat, disconnectFromChat };
};

export const useChatSubscriptions = (
  handleConnectionStatus,
  handleOrderChat,
  handlePartnerChat,
  handleOrderMessageChat,
) => {
  useEffect(() => {
    const unsubscribeConnection = chatHub.subscribe("connection-status", handleConnectionStatus);
    const unsubscribeOrderChat = chatHub.subscribe("order-chat", handleOrderChat);
    const unsubscribePartnerChat = chatHub.subscribe("partner-chat", handlePartnerChat);
    const unsubscribeOrderMessageChat = chatHub.subscribe("order-message-chat", handleOrderMessageChat);

    return () => {
      unsubscribeConnection();
      unsubscribeOrderChat();
      unsubscribePartnerChat();
      unsubscribeOrderMessageChat();
    };
  }, [handleConnectionStatus, handleOrderChat, handlePartnerChat, handleOrderMessageChat]);
};

export const useChatAutoConnect = (connectToChat) => {
  useEffect(() => {
    connectToChat();
  }, [connectToChat]);
};

export const useChatCleanup = () => {
  useEffect(() => {
    return () => {
      chatHub.disconnect({ preserveCallbacks: true });
    };
  }, []);
};

export const useRefreshChatCallback = (state, actions) => {
  const setRefreshCallback = useCallback(
    (callback) => {
      actions.setRefreshCallback(callback);

      if (callback && state.chatOrderId) {
        actions.registerChat(state.chatOrderId, callback, true);
      }
    },
    [state.chatOrderId, actions],
  );

  const refreshChat = useCallback(
    async (orderId) => {
      if (state.refreshChatCallback) {
        try {
          await state.refreshChatCallback();
        } catch (error) {
          console.error("Error refreshing chat:", error);
        }
      }
    },
    [state.refreshChatCallback],
  );

  return { setRefreshCallback, refreshChat };
};

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChat must be used within a ChatProvider");
  }
  return context;
};
